import type { Point2 } from '@frontend/viewer-core';

import type {
  MeasureModifiers,
  MeasureTarget,
  Measurement,
  MeasurementKind,
} from './types';

/**
 * How far apart two lines' directions may be and still count as parallel.
 *
 * A tenth of a degree. DXF coordinates are often rounded, and on a short line
 * that rounding alone tilts it by more than 1e-4 rad, so a tighter tolerance
 * would let lines drawn parallel fall back to point to point.
 */
const PARALLEL_TOLERANCE_RAD = (0.1 * Math.PI) / 180;
const SIN_PARALLEL_TOLERANCE = Math.sin(PARALLEL_TOLERANCE_RAD);

/** The eight 45° directions, exact, so a locked axis carries no rounding. */
const ORTHO_DIRECTIONS: readonly Point2[] = [
  [1, 0],
  [Math.SQRT1_2, Math.SQRT1_2],
  [0, 1],
  [-Math.SQRT1_2, Math.SQRT1_2],
  [-1, 0],
  [-Math.SQRT1_2, -Math.SQRT1_2],
  [0, -1],
  [Math.SQRT1_2, -Math.SQRT1_2],
];

type MeasureOptions = Partial<MeasureModifiers> & {
  /**
   * Distance within which two points are the same point: concentric centres,
   * or a perpendicular too short to mean anything. See
   * `DrawingModel.samePointTolerance`.
   */
  readonly tolerance: number;
};

type Segment = readonly [Point2, Point2];

const distanceBetween = (a: Point2, b: Point2): number =>
  Math.hypot(b[0] - a[0], b[1] - a[1]);

/** Foot of the perpendicular from `point` to the infinite line through `segment`. */
const footOnLine = (point: Point2, [a, b]: Segment): Point2 => {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq === 0) {
    return a;
  }
  const t = ((point[0] - a[0]) * dx + (point[1] - a[1]) * dy) / lengthSq;
  return [a[0] + t * dx, a[1] + t * dy];
};

const isParallel = ([a1, b1]: Segment, [a2, b2]: Segment): boolean => {
  const ux = b1[0] - a1[0];
  const uy = b1[1] - a1[1];
  const vx = b2[0] - a2[0];
  const vy = b2[1] - a2[1];
  const lengths = Math.hypot(ux, uy) * Math.hypot(vx, vy);
  return (
    lengths > 0 &&
    Math.abs(ux * vy - uy * vx) / lengths <= SIN_PARALLEL_TOLERANCE
  );
};

const isSamePoint = (a: Point2, b: Point2): boolean =>
  a[0] === b[0] && a[1] === b[1];

/**
 * The same line or circle, clicked twice.
 *
 * The key alone is not enough: a MINSERT grid repeats one block entity under a
 * single key, and each copy is a different line. A circle is judged by its
 * geometry alone, because a hole drawn as arcs is one circle under several keys.
 */
const isSameTarget = (from: MeasureTarget, to: MeasureTarget): boolean => {
  if (from.kind === 'line' && to.kind === 'line') {
    return (
      from.entityKey === to.entityKey &&
      isSamePoint(from.segment[0], to.segment[0]) &&
      isSamePoint(from.segment[1], to.segment[1])
    );
  }
  if (from.kind === 'circle' && to.kind === 'circle') {
    return isSamePoint(from.center, to.center) && from.radius === to.radius;
  }
  return false;
};

/** Where a target measures from when the other side is not a line. */
const anchorOf = (target: MeasureTarget): Point2 =>
  target.kind === 'circle' ? target.center : target.spot;

/** `target`, moved onto the nearest of the 45° rays out of `origin`. */
const orthoLock = (origin: Point2, target: Point2): Point2 => {
  const dx = target[0] - origin[0];
  const dy = target[1] - origin[1];
  const octant = Math.round(Math.atan2(dy, dx) / (Math.PI / 4));
  const count = ORTHO_DIRECTIONS.length;
  const [ux, uy] = ORTHO_DIRECTIONS[(octant + count) % count];
  const along = dx * ux + dy * uy;
  return [origin[0] + ux * along, origin[1] + uy * along];
};

/** `distance` along the ray from `origin` through `through`. */
const alongRay = (
  origin: Point2,
  through: Point2,
  distance: number,
): Point2 => {
  const length = distanceBetween(origin, through);
  const [ux, uy] =
    length > 0
      ? [(through[0] - origin[0]) / length, (through[1] - origin[1]) / length]
      : [1, 0];
  return [origin[0] + ux * distance, origin[1] + uy * distance];
};

/**
 * What a measurement from one target to another spans.
 *
 * A measurement starts from `from`. Perpendiculars drop only between parallel
 * lines and from a circle's centre to a line. Lines are infinite, so a
 * perpendicular may land past the end of the line it drops onto. A point or a
 * spot on a curve never drops one, which could land far outside the part.
 * Pairs with no caliper
 * rule measure the straight distance between the two click spots, as does a
 * perpendicular that would be zero.
 *
 * The modifier keys take the caliper rules off. Both measure point to point
 * from where `from` measures from, which is its centre for a circle and its
 * click spot otherwise. A free point ends at `to`'s spot; the ortho lock moves
 * the end onto the nearest 45° from the start.
 */
export const measureBetween = (
  from: MeasureTarget,
  to: MeasureTarget,
  { tolerance, isOrthoLocked = false, isFreePoint = false }: MeasureOptions,
): Measurement => {
  const span = (kind: MeasurementKind, start: Point2, end: Point2) => ({
    kind,
    from,
    to,
    start,
    end,
    distance: distanceBetween(start, end),
  });
  const betweenSpots = () => span('pointToPoint', from.spot, to.spot);

  /** The foot of the perpendicular, or `null` when it would be zero. */
  const perpendicular = (anchor: Point2, segment: Segment): Point2 | null => {
    const foot = footOnLine(anchor, segment);
    return distanceBetween(anchor, foot) > tolerance ? foot : null;
  };

  if (isOrthoLocked) {
    const start = anchorOf(from);
    return span('orthoLocked', start, orthoLock(start, anchorOf(to)));
  }
  if (isFreePoint) {
    return span('pointToPoint', anchorOf(from), to.spot);
  }

  if (isSameTarget(from, to)) {
    return span('sameTarget', from.spot, to.spot);
  }

  if (from.kind === 'line' && to.kind === 'line') {
    const foot = isParallel(from.segment, to.segment)
      ? perpendicular(from.spot, to.segment)
      : null;
    return foot ? span('parallelGap', from.spot, foot) : betweenSpots();
  }

  if (from.kind === 'circle' && to.kind === 'circle') {
    if (distanceBetween(from.center, to.center) > tolerance) {
      return span('centerToCenter', from.center, to.center);
    }
    return span(
      'concentricGap',
      alongRay(from.center, from.spot, from.radius),
      alongRay(from.center, from.spot, to.radius),
    );
  }

  if (from.kind === 'circle' && to.kind === 'line') {
    const foot = perpendicular(from.center, to.segment);
    return foot ? span('centerToLine', from.center, foot) : betweenSpots();
  }

  if (from.kind === 'line' && to.kind === 'circle') {
    const foot = perpendicular(to.center, from.segment);
    return foot ? span('centerToLine', foot, to.center) : betweenSpots();
  }

  if (from.kind === 'circle' || to.kind === 'circle') {
    return span('pointToCenter', anchorOf(from), anchorOf(to));
  }

  return betweenSpots();
};
